import { CalendarTaskSync } from '@griply/types';
import React, { useMemo } from 'react';
import { Tag as TagGlyph } from 'shared/assets/icons';
import { TaskCardContextMenu } from 'shared/components/connected/task-card-context-menu';
import { Icon } from 'shared/components/ui/icon';
import { useContextMenuState } from 'shared/hooks/use-context-menu-state';
import { ID } from 'shared/types/id';
import { Tag } from 'shared/types/tag';
import { Task } from 'shared/types/task';

import * as Styled from './event.style';
import { EventCard } from './event-card';
import { TaskEventSyncBadge } from './task-event-sync-badge';

export type TaskTagEventProps = {
  task: Task;
  tags: Tag[];
  onClick?: (id: ID) => void;
  numberOfLines: number;
  timeText?: string;
  completed?: boolean;
  onInteractionComplete?: () => void;
  calendarSync?: CalendarTaskSync;
};

// Acceptable parallel of TaskLifeAreaEvent/TaskSmartListEvent (reuse-over-reinvent.md):
// one calendar grid card per timeslot entity; they share the EventCard shape but
// differ on the leading icon and color source.
export const TaskTagEvent: React.FC<TaskTagEventProps> = ({
  task,
  tags,
  onClick,
  numberOfLines,
  timeText,
  completed,
  onInteractionComplete,
  calendarSync,
}) => {
  const {
    open: openMenu,
    close: closeMenu,
    position: menuPosition,
  } = useContextMenuState();
  const _onClick = () => onClick?.(task.id);

  const tag = useMemo(
    () => tags.find(({ id }) => id === task.action?.meta.id),
    [tags, task.action?.meta.id],
  );

  if (!tag) {
    return null;
  }

  return (
    <>
      <EventCard
        title={tag.name}
        color={tag.color}
        timeText={timeText}
        completed={completed}
        numberOfLines={numberOfLines}
        onClick={_onClick}
        onContextMenu={openMenu}
        trailing={
          <TaskEventSyncBadge
            task={task}
            calendarSync={calendarSync}
            color={tag.color}
          />
        }
        leading={
          <Styled.LeadingSlot $color={tag.color} $completed={completed}>
            <Icon icon={TagGlyph} />
          </Styled.LeadingSlot>
        }
      />

      {!!menuPosition && (
        <TaskCardContextMenu
          task={task}
          onClose={closeMenu}
          location={menuPosition}
          onAction={onInteractionComplete}
          showCalendarSync
        />
      )}
    </>
  );
};
